// MobileSummaryBar.tsx
import React, { useState } from "react";
import { Professional, Service, Location } from "./types";

interface MobileSummaryBarProps {
  professional: Professional;
  selectedService: Service | null;
  selectedLocation: Location | null;
  selectedDate: string | null;
  selectedTime: string | null;
  onConfirm: () => void;
}

const MobileSummaryBar: React.FC<MobileSummaryBarProps> = ({
  professional,
  selectedService,
  selectedLocation,
  selectedDate,
  selectedTime,
  onConfirm,
}) => {
  const [showModal, setShowModal] = useState(false);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("es-ES", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  };

  const isFormComplete =
    selectedService && selectedLocation && selectedDate && selectedTime;

  const getStatusMessage = () => {
    if (!selectedService) return "Selecciona un servicio";
    if (!selectedLocation) return "Selecciona una ubicación";
    if (!selectedDate) return "Selecciona una fecha";
    if (!selectedTime) return "Selecciona una hora";
    return "¡Confimar tu cita! ";
  };

  const getSummaryText = () => {
    if (!selectedService) return "Sin servicio seleccionado";

    const parts = [];
    if (selectedDate && selectedTime) {
      parts.push(`${formatDate(selectedDate)} ${selectedTime}`);
    }
    if (selectedLocation) {
      parts.push(selectedLocation.name);
    }
    return parts.length > 0
      ? parts.join(" • ")
      : "Selecciona fecha y ubicación";
  };

  const isStatusIncomplete =
    !selectedService || !selectedLocation || !selectedDate || !selectedTime;

  return (
    <>
      <div className="mobile-summary-bar">
        <div className="mobile-summary-content">
          <div className="mobile-summary-info">
            <div className="mobile-summary-service">
              {selectedService
                ? selectedService.name
                : "Selecciona un servicio"}
            </div>
            <div className="mobile-summary-details" title={getSummaryText()}>
              {getSummaryText()}
            </div>
            <div
              className={`mobile-summary-status ${
                isStatusIncomplete ? "incomplete" : "complete"
              }`}
            >
              {getStatusMessage()}
            </div>
          </div>

          <div
            style={{
              display: "flex",
              gap: "var(--space-sm)",
              alignItems: "center",
            }}
          >
            <button
              className="mobile-summary-toggle"
              onClick={() => setShowModal(true)}
              title="Ver resumen completo"
            >
              {/* List icon */}
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8 6H21"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M8 12H21"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M8 18H21"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M3 6H3.01"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M3 12H3.01"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M3 18H3.01"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            <div className="mobile-summary-price">
              {selectedService ? `$${selectedService.price.toFixed(2)}` : "-"}
            </div>

            <button
              className="btn btn--primary"
              onClick={onConfirm}
              disabled={!isFormComplete}
              style={{
                padding: "0.5rem 1rem",
                fontSize: "0.9rem",
                whiteSpace: "nowrap",
                minWidth: "100px",
              }}
            >
              Confirmar
            </button>
          </div>
        </div>
      </div>

      {/* Modal for full summary */}
      {showModal && (
        <div className="mobile-summary-modal">
          <div className="mobile-summary-modal-content">
            <div className="mobile-summary-modal-header">
              <h3 className="mobile-summary-modal-title">Resumen de Cita</h3>
              <button
                className="mobile-summary-modal-close"
                onClick={() => setShowModal(false)}
              >
                ×
              </button>
            </div>

            <div
              className="card"
              style={{
                marginBottom: "var(--space-md)",
                boxShadow: "none",
                border: "1px solid var(--neutral-200)",
              }}
            >
              <p>
                <strong>Profesional:</strong> {professional.name}
              </p>
              <p>
                <strong>Especialidad:</strong> {professional.specialty}
              </p>
            </div>

            {selectedService ? (
              <div
                className="card"
                style={{
                  marginBottom: "var(--space-md)",
                  boxShadow: "none",
                  border: "1px solid var(--neutral-200)",
                }}
              >
                <p style={{ color: "var(--color-accent)", fontWeight: 700 }}>
                  Servicio
                </p>
                <p>
                  <strong>Tipo:</strong> {selectedService.name}
                </p>
                <p>
                  <strong>Duración:</strong> {selectedService.duration} min
                </p>
                <p>
                  <strong>Precio:</strong> ${selectedService.price.toFixed(2)}
                </p>
              </div>
            ) : (
              <div
                className="card"
                style={{
                  marginBottom: "var(--space-md)",
                  boxShadow: "none",
                  backgroundColor: "rgba(220, 53, 69, 0.05)",
                  border: "1px solid rgba(220, 53, 69, 0.2)",
                }}
              >
                <p style={{ color: "var(--error)", fontWeight: 700 }}>
                  ⚠️ Servicio no seleccionado
                </p>
                <p>Por favor selecciona un servicio para continuar</p>
              </div>
            )}

            {selectedLocation ? (
              <div
                className="card"
                style={{
                  marginBottom: "var(--space-md)",
                  boxShadow: "none",
                  border: "1px solid var(--neutral-200)",
                }}
              >
                <p style={{ color: "var(--color-accent)", fontWeight: 700 }}>
                  Ubicación
                </p>
                <p>
                  <strong>Lugar:</strong> {selectedLocation.name}
                </p>
                <p>
                  <strong>Dirección:</strong> {selectedLocation.address}
                </p>
                <p>
                  <strong>Horario:</strong> {selectedLocation.schedule}
                </p>
              </div>
            ) : (
              <div
                className="card"
                style={{
                  marginBottom: "var(--space-md)",
                  boxShadow: "none",
                  backgroundColor: "rgba(220, 53, 69, 0.05)",
                  border: "1px solid rgba(220, 53, 69, 0.2)",
                }}
              >
                <p style={{ color: "var(--error)", fontWeight: 700 }}>
                  ⚠️ Ubicación no seleccionada
                </p>
                <p>Por favor selecciona una ubicación para continuar</p>
              </div>
            )}

            {selectedDate && selectedTime ? (
              <div
                className="card"
                style={{
                  marginBottom: "var(--space-md)",
                  boxShadow: "none",
                  border: "1px solid var(--neutral-200)",
                }}
              >
                <p style={{ color: "var(--color-accent)", fontWeight: 700 }}>
                  Fecha y Hora
                </p>
                <p>
                  <strong>Día:</strong> {formatDate(selectedDate)}
                </p>
                <p>
                  <strong>Hora:</strong> {selectedTime}
                </p>
              </div>
            ) : (
              <div
                className="card"
                style={{
                  marginBottom: "var(--space-md)",
                  boxShadow: "none",
                  backgroundColor: "rgba(220, 53, 69, 0.05)",
                  border: "1px solid rgba(220, 53, 69, 0.2)",
                }}
              >
                <p style={{ color: "var(--error)", fontWeight: 700 }}>
                  ⚠️ Fecha/Hora no seleccionadas
                </p>
                <p>Por favor selecciona una fecha y hora para continuar</p>
              </div>
            )}

            <button
              className="btn btn--primary"
              style={{
                width: "100%",
                justifyContent: "center",
                marginTop: "var(--space-md)",
              }}
              onClick={() => {
                setShowModal(false);
                onConfirm();
              }}
              disabled={!isFormComplete}
            >
              Confirmar Cita
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default MobileSummaryBar;
